import { lazy, Suspense, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Helmet } from 'react-helmet';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useComputedColorScheme, useMantineColorScheme } from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import { ModalsProvider } from '@mantine/modals';
import { BackgroundQueue } from './components/BackgroundQueue/BackgroundQueue';
import { ChangelogButton } from './components/ChangelogButton/ChangelogButton';
import { InternalPrivateRoute } from './components/InternalPrivateRoute/InternalPrivateRoute';
import { OnboardingAppLayout } from './components/PageLayout/OnboardingAppLayout';
import { PublicAppLayout } from './components/PageLayout/PublicAppLayout';
import { PostRoute } from './components/Posts/PostRoute/PostRoute';
import { PrivateRoute } from './components/PrivateRoute/PrivateRoute';
import ErrorBoundary from './ErrorBoundary';
import { StoreProvider } from './stores/provider';
import { Layout } from './ui';

// Dynamic imports for pages
const NotFound = lazy(() =>
  import('./pages/NotFound').then((module) => ({ default: module.NotFound }))
);
const OnboardingIndexPage = lazy(() =>
  import('./pages/Onboarding/Index').then((module) => ({
    default: module.OnboardingIndexPage,
  }))
);
const ClientOnboardingPage = lazy(() =>
  import('./pages/Onboarding/Client').then((module) => ({
    default: module.ClientOnboardingPage,
  }))
);
const OnboardingAgencyPage = lazy(() =>
  import('./pages/Onboarding/Agency').then((module) => ({
    default: module.OnboardingAgencyPage,
  }))
);
const HomePage = lazy(() =>
  import('./pages/Home/page').then((module) => ({ default: module.HomePage }))
);
const DashboardPage = lazy(() =>
  import('./pages/Dashboard/page').then((module) => ({ default: module.DashboardPage }))
);
const AllPostsPage = lazy(() =>
  import('./pages/AllPosts/page').then((module) => ({ default: module.AllPostsPage }))
);
const PendingPublishPage = lazy(() =>
  import('./pages/PendingPublish/page').then((module) => ({
    default: module.PendingPublishPage,
  }))
);
const InsightsPage = lazy(() =>
  import('./pages/Insights/page').then((module) => ({ default: module.InsightsPage }))
);
const FileStoragePage = lazy(() =>
  import('./pages/FileStorage/page').then((module) => ({ default: module.FileStoragePage }))
);
const DocumentPage = lazy(() =>
  import('./pages/Document/page').then((module) => ({ default: module.DocumentPage }))
);
const CalendarPage = lazy(() =>
  import('./pages/Calendar/page').then((module) => ({
    default: module.CalendarPage,
  }))
);
const CalendarTodayPage = lazy(() =>
  import('./pages/Calendar/TodayPage').then((module) => ({
    default: module.CalendarTodayPage,
  }))
);
const PostPage = lazy(() =>
  import('./pages/Post/page').then((module) => ({ default: module.PostPage }))
);
const PostV2Page = lazy(() =>
  import('./pages/PostV2/page').then((module) => ({ default: module.PostV2Page }))
);
const LoginPage = lazy(() =>
  import('./pages/Auth/Login').then((module) => ({ default: module.LoginPage }))
);
const GoogleCallbackPage = lazy(() =>
  import('./pages/Auth/GoogleCallback').then((module) => ({
    default: module.GoogleCallbackPage,
  }))
);
const LinkedInCallbackPage = lazy(() =>
  import('./pages/Auth/LinkedInCallback').then((module) => ({
    default: module.LinkedInCallbackPage,
  }))
);
const LogoutPage = lazy(() =>
  import('./pages/Auth/LogoutPage').then((module) => ({
    default: module.LogoutPage,
  }))
);
const AdminTrelloPage = lazy(() =>
  import('./pages/Admin/Trello').then((module) => ({
    default: module.AdminTrelloPage,
  }))
);
const AdminPlatformsPage = lazy(() =>
  import('./pages/Admin/Platforms').then((module) => ({
    default: module.AdminPlatformsPage,
  }))
);
const AdminUsersPage = lazy(() =>
  import('./pages/Admin/Users').then((module) => ({
    default: module.AdminUsersPage,
  }))
);
const AdminApiTokensPage = lazy(() =>
  import('./pages/Admin/ApiTokens').then((module) => ({
    default: module.AdminApiTokensPage,
  }))
);
const AdminFeatureFlagsPage = lazy(() =>
  import('./pages/Admin/FeatureFlags').then((module) => ({
    default: module.AdminFeatureFlagsPage,
  }))
);
const NewChatPage = lazy(() =>
  import('./pages/Ai/NewChatPage').then((module) => ({
    default: module.NewChatPage,
  }))
);
const ChatPage = lazy(() =>
  import('./pages/Ai/ChatPage').then((module) => ({
    default: module.ChatPage,
  }))
);
const AiVideosPage = lazy(() =>
  import('./pages/Ai/AiVideosPage').then((module) => ({
    default: module.AiVideosPage,
  }))
);
const InboxLayout = lazy(() =>
  import('./pages/Inbox/InboxLayout').then((module) => ({
    default: module.InboxLayout,
  }))
);
const InboxPage = lazy(() =>
  import('./pages/Inbox/InboxPage').then((module) => ({
    default: module.InboxPage,
  }))
);
const InboxPostPage = lazy(() =>
  import('./pages/Inbox/InboxPostPage').then((module) => ({
    default: module.InboxPostPage,
  }))
);
const SettingsAgencyPage = lazy(() =>
  import('./pages/Settings/SettingsAgencyPage').then((module) => ({
    default: module.SettingsAgencyPage,
  }))
);
const SettingsAIPage = lazy(() =>
  import('./pages/Settings/SettingsAIPage').then((module) => ({
    default: module.SettingsAIPage,
  }))
);
const SettingsHolidaysPage = lazy(() =>
  import('./pages/Settings/SettingsHolidaysPage').then((module) => ({
    default: module.SettingsHolidaysPage,
  }))
);
const SettingsUserPage = lazy(() =>
  import('./pages/Settings/SettingsUserPage').then((module) => ({
    default: module.SettingsUserPage,
  }))
);
const SettingsClientsPage = lazy(() =>
  import('./pages/Settings/SettingsClientsPage').then((module) => ({
    default: module.SettingsClientsPage,
  }))
);
const SettingsIntegrationsPage = lazy(() =>
  import('./pages/Settings/SettingsIntegrationsPage').then((module) => ({
    default: module.SettingsIntegrationsPage,
  }))
);
const SettingsSlackNotificationsPage = lazy(() =>
  import('./pages/Settings/SettingsSlackNotificationsPage').then((module) => ({
    default: module.SettingsSlackNotificationsPage,
  }))
);
const SettingsMcpPage = lazy(() =>
  import('./pages/Settings/SettingsMcpPage').then((module) => ({
    default: module.SettingsMcpPage,
  }))
);
const SettingsTemplatesPage = lazy(() =>
  import('./pages/Settings/SettingsTemplatesPage').then((module) => ({
    default: module.SettingsTemplatesPage,
  }))
);
const SettingsUsersPage = lazy(() =>
  import('./pages/Settings/SettingsUsersPage').then((module) => ({
    default: module.SettingsUsersPage,
  }))
);
const ClientHomePage = lazy(() =>
  import('./pages/Client/ClientHomePage').then((module) => ({
    default: module.ClientHomePage,
  }))
);
const ClientSettingsPage = lazy(() =>
  import('./pages/Client/ClientSettingsPage').then((module) => ({
    default: module.ClientSettingsPage,
  }))
);
const ClientPlannerPage = lazy(() =>
  import('./pages/Client/ClientPlannerPage').then((module) => ({
    default: module.ClientPlannerPage,
  }))
);
const ClientOnboardingPageInternal = lazy(() =>
  import('./pages/Client/ClientOnboardingPage').then((module) => ({
    default: module.ClientOnboardingPage,
  }))
);
const ClientLearnPage = lazy(() =>
  import('./pages/Client/ClientLearnPage').then((module) => ({
    default: module.ClientLearnPage,
  }))
);
const ClientCompetitorsPage = lazy(() =>
  import('./pages/Client/ClientCompetitorsPage').then((module) => ({
    default: module.ClientCompetitorsPage,
  }))
);
const ClientAreaDashboardPage = lazy(() =>
  import('./pages/ClientArea/ClientAreaDashboardPage').then((module) => ({
    default: module.ClientAreaDashboardPage,
  }))
);
const ClientAreaPage = lazy(() =>
  import('./pages/ClientArea/ClientAreaPage').then((module) => ({
    default: module.ClientAreaPage,
  }))
);
const ClientAreaFeedPage = lazy(() =>
  import('./pages/ClientArea/ClientAreaFeedPage').then((module) => ({
    default: module.ClientAreaFeedPage,
  }))
);
const ClientAreaTasksPage = lazy(() =>
  import('./pages/ClientArea/ClientAreaTasksPage').then((module) => ({
    default: module.ClientAreaTasksPage,
  }))
);
const PrivacyPage = lazy(() =>
  import('./pages/Legal/PrivacyPage').then((module) => ({ default: module.PrivacyPage }))
);
const TermsPage = lazy(() =>
  import('./pages/Legal/TermsPage').then((module) => ({ default: module.TermsPage }))
);

// Loading component for Suspense fallback
const PageLoader = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontSize: '16px',
      color: '#666',
    }}
  >
    Loading...
  </div>
);

function makePage(Component: React.ComponentType, title?: string) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {title && <Helmet title={title} />}
        <Component />
      </Suspense>
    </ErrorBoundary>
  );
}

export const App = observer(function App() {
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme('light');

  useHotkeys([['Mod+J', () => setColorScheme(computedColorScheme === 'light' ? 'dark' : 'light')]]);

  useEffect(() => {
    // @ts-expect-error Canny
    if (typeof Canny !== 'undefined') {
      // @ts-expect-error Canny
      Canny('initChangelog', {
        appID: '68bdf527c08f0b6e465049a5',
        position: 'top',
        align: 'left',
        theme: 'auto',
      });
    }
  }, []);

  return (
    <Routes>
      <Route
        element={
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <StoreProvider>
                <ModalsProvider>
                  <Outlet />
                  <ChangelogButton />
                </ModalsProvider>
              </StoreProvider>
            </Suspense>
          </ErrorBoundary>
        }
      >
        <Route path="/" element={<Navigate to="/home" />} />
        <Route path="/auth/login" element={makePage(LoginPage)} />
        <Route path="/auth/logout" element={makePage(LogoutPage)} />
        <Route path="/auth/callback/google" element={<GoogleCallbackPage />} />
        <Route path="/auth/callback/linkedin" element={<LinkedInCallbackPage />} />
        <Route path="/onboarding" element={<OnboardingAppLayout />}>
          <Route index element={makePage(OnboardingIndexPage)} />
          <Route path="client" element={makePage(ClientOnboardingPage)} />
          <Route path="agency/:agencyId" element={makePage(OnboardingAgencyPage)} />
        </Route>
        <Route
          path="/admin"
          element={
            <InternalPrivateRoute>
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            </InternalPrivateRoute>
          }
        >
          <Route index element={<Navigate to="/admin/trello" />} />
          <Route path="trello" element={<AdminTrelloPage />} />
          <Route path="platforms" element={<AdminPlatformsPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="api-tokens" element={<AdminApiTokensPage />} />
          <Route path="feature-flags" element={<AdminFeatureFlagsPage />} />
        </Route>
        <Route
          element={
            <PrivateRoute>
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
              <PostRoute />
              <BackgroundQueue />
            </PrivateRoute>
          }
        >
          <Route path="/home" element={makePage(HomePage)} />
          <Route path="/dashboard" element={makePage(DashboardPage)} />
          <Route path="/posts" element={makePage(AllPostsPage)} />
          <Route path="/posts/pending-publish" element={makePage(PendingPublishPage)} />
          {/* Intentionally not in Navbar.tsx — direct-link only for now. */}
          <Route path="/insights" element={makePage(InsightsPage)} />
          <Route path="/file-storage" element={makePage(FileStoragePage)} />
          <Route path="/documents/:documentId" element={makePage(DocumentPage)} />
          <Route path="/calendar" element={makePage(CalendarPage)} />
          <Route path="/calendar/today" element={makePage(CalendarTodayPage)} />
          <Route path="/inbox" element={<InboxLayout />}>
            <Route index element={makePage(InboxPage)} />
            <Route path="post/:postId" element={makePage(InboxPostPage)} />
          </Route>
          <Route path="/ai/new" element={makePage(NewChatPage)} />
          <Route path="/ai/:chatId" element={makePage(ChatPage)} />
          <Route path="/ai-videos" element={makePage(AiVideosPage)} />
          <Route path="/ai" element={<Navigate to="/ai/new" />} />
          <Route path="/post/:postId" element={makePage(PostPage)} />
          <Route path="/post2/:postId" element={makePage(PostV2Page)} />
          <Route path="/client-area/:clientId" element={makePage(ClientAreaDashboardPage)} />
          <Route path="/client-area/:clientId/calendar" element={makePage(ClientAreaPage)} />
          <Route path="/client-area/:clientId/feed" element={makePage(ClientAreaFeedPage)} />
          <Route path="/client-area/:clientId/tasks" element={makePage(ClientAreaTasksPage)} />
          <Route path="/client/:clientId" element={makePage(ClientHomePage)} />
          <Route path="/client/:clientId/competitors" element={makePage(ClientCompetitorsPage)} />
          <Route path="/client/:clientId/settings" element={makePage(ClientSettingsPage)} />
          <Route path="/client/:clientId/planner/:planId?" element={makePage(ClientPlannerPage)} />
          <Route
            path="/client/:clientId/onboarding"
            element={makePage(ClientOnboardingPageInternal)}
          />
          <Route path="/client/:clientId/learn" element={makePage(ClientLearnPage)} />
          <Route path="/settings" element={<Navigate to="/settings/user" />} />
          <Route path="/settings/agency" element={<SettingsAgencyPage />} />
          <Route path="/settings/ai" element={<SettingsAIPage />} />
          <Route path="/settings/user" element={<SettingsUserPage />} />
          <Route path="/settings/users" element={<SettingsUsersPage />} />
          <Route path="/settings/clients" element={<SettingsClientsPage />} />
          <Route path="/settings/integrations" element={<SettingsIntegrationsPage />} />
          <Route
            path="/settings/slack-notifications"
            element={<SettingsSlackNotificationsPage />}
          />
          <Route path="/settings/mcp" element={<SettingsMcpPage />} />
          <Route path="/settings/templates" element={<SettingsTemplatesPage />} />
          <Route path="/settings/holidays" element={<SettingsHolidaysPage />} />
        </Route>

        <Route
          element={
            <Suspense fallback={<PageLoader />}>
              <Layout>
                <Outlet />
              </Layout>
            </Suspense>
          }
        >
          <Route path="/client-area/share/:token" element={makePage(ClientAreaDashboardPage)} />
          <Route path="/client-area/share/:token/calendar" element={makePage(ClientAreaPage)} />
          <Route path="/client-area/share/:token/feed" element={makePage(ClientAreaFeedPage)} />
          <Route path="/client-area/share/:token/tasks" element={makePage(ClientAreaTasksPage)} />
        </Route>

        <Route element={<PublicAppLayout />}>
          <Route
            path="/:lang/privacy"
            element={makePage(PrivacyPage, 'Privacy Policy – Ametista AI')}
          />
          <Route path="/:lang/terms" element={makePage(TermsPage, 'Terms of Use – Ametista AI')} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
});
